/**
 * Ephemeral UI state: which row is being edited (one at a time) and where its caret goes.
 * Focus changes are applied synchronously (flushSync) so iOS keeps the keyboard open when
 * Enter creates a new row.
 */
import { flushSync } from 'react-dom'
import { useSyncExternalStore } from 'react'
import { sync } from '../sync/doc'

export interface EditState {
  id: string | null
  /** Row instance key (the same item can render in several places, e.g. Board + Days). */
  scope: string | null
  caret: number | 'end' | 'start'
  seq: number
}

let state: EditState = { id: null, scope: null, caret: 'end', seq: 0 }
const subs = new Set<() => void>()
const inputs = new Map<string, HTMLTextAreaElement>()

export const editStore = {
  get: () => state,
  subscribe: (fn: () => void) => {
    subs.add(fn)
    return () => subs.delete(fn)
  },
}

function emit() {
  for (const f of subs) f()
}

const keyOf = (scope: string, id: string) => `${scope}::${id}`

export function registerInput(scope: string, id: string, el: HTMLTextAreaElement | null) {
  const k = keyOf(scope, id)
  if (el) inputs.set(k, el)
  else inputs.delete(k)
}

/** Enter edit mode for a row and focus its textarea synchronously. */
export function startEditing(scope: string, id: string, caret: EditState['caret'] = 'end') {
  flushSync(() => {
    state = { id, scope, caret, seq: state.seq + 1 }
    emit()
  })
  const el = inputs.get(keyOf(scope, id))
  if (el) {
    el.focus({ preventScroll: true })
    const pos = caret === 'end' ? el.value.length : caret === 'start' ? 0 : Math.min(caret, el.value.length)
    try {
      el.setSelectionRange(pos, pos)
    } catch {
      /* ignore */
    }
    requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }))
  }
  sync.setEditing(id)
}

export function stopEditing(id?: string) {
  if (id && state.id !== id) return
  if (!state.id) return
  state = { id: null, scope: null, caret: 'end', seq: state.seq + 1 }
  emit()
  sync.setEditing(null)
}

// Moving the row that's being edited (drag) without dropping the phone keyboard: for the
// drag, focus sits in an invisible "keeper" field (so the keyboard stays up and the layout
// doesn't jump), the row keeps its editing pill, and on drop the caret goes back into the
// row wherever it landed — inside the finger-up, so iOS keeps the keyboard.
let held: { id: string; scope: string; caret: number } | null = null
let keeper: HTMLTextAreaElement | null = null

/** True while `id` is the edited row being dragged (its blur must not end editing). */
export function isEditHeld(id: string): boolean {
  return held?.id === id
}

/** Start of a drag: if the dragged row is being edited, park focus and keep the pill. */
export function holdEditForDrag(id: string): boolean {
  if (state.id !== id || !state.scope) return false
  const ta = document.activeElement instanceof HTMLTextAreaElement ? document.activeElement : null
  held = { id, scope: state.scope, caret: ta?.selectionStart ?? Number.MAX_SAFE_INTEGER }
  if (!keeper) {
    keeper = document.createElement('textarea')
    keeper.setAttribute('aria-hidden', 'true')
    keeper.tabIndex = -1
    // 16px so iOS doesn't zoom; tiny, invisible and out of the way.
    Object.assign(keeper.style, { position: 'fixed', top: '0', left: '0', width: '1px', height: '1px', opacity: '0', fontSize: '16px', border: '0', padding: '0', pointerEvents: 'none' })
    document.body.appendChild(keeper)
  }
  keeper.focus({ preventScroll: true })
  return true
}

/** End of a drag (after the move is applied): put the caret back into the row. */
export function releaseEditAfterDrag() {
  const h = held
  held = null
  if (h) startEditing(h.scope, h.id, h.caret)
}

/** Only the row being edited (and the one that stops) re-renders: the snapshot is per-row. */
export function useEditing(scope: string, id: string): EditState | null {
  const seq = useSyncExternalStore(editStore.subscribe, () => (state.id === id && state.scope === scope ? state.seq : -1))
  return seq === -1 ? null : state
}

export function useAnyEditing(): boolean {
  return useSyncExternalStore(editStore.subscribe, () => state.id !== null)
}

// Drag/swipe coordination: while one gesture owns the pointer, others stand down.
let gesture: 'none' | 'drag' | 'swipe' = 'none'
export const gestureState = {
  get: () => gesture,
  set: (g: typeof gesture) => {
    gesture = g
  },
}
